import { useEffect, useState } from 'react';
import { getLocationTracker, type TrackerStatus } from '../services/locationTracker';
import { getLocationTrackingSettings } from '../services/locationTrackingService';
import type { LocationTrackingSettings } from '../types/locationTracking';
import {
  isAttendanceSessionOpen,
  isTrackedAttendanceSource,
} from '../utils/locationTracking';

let settingsRequest: Promise<LocationTrackingSettings | null> | null = null;

/** Server tracking settings, fetched once per app session. */
export function loadTrackingSettings(): Promise<LocationTrackingSettings | null> {
  if (!settingsRequest) {
    settingsRequest = getLocationTrackingSettings().catch(() => {
      settingsRequest = null;
      return null;
    });
  }
  return settingsRequest;
}

export function useLocationTrackerStatus(): TrackerStatus {
  const tracker = getLocationTracker();
  const [status, setStatus] = useState(tracker.getStatus());
  useEffect(() => tracker.subscribe(setStatus), [tracker]);
  return status;
}

type AttendanceLike = {
  attendanceId?: number | null;
  attendanceMode: string | null;
  checkInTime: string | null;
  checkOutTime: string | null;
  isCheckedOut?: boolean;
};

/**
 * Keeps the tracker in step with today's attendance: running for an open
 * tracked check-in, stopped once that attendance is checked out. Office/Kiosk
 * and WFH attendance never start it.
 */
export function useAttendanceLocationTracking(
  attendance: AttendanceLike | null | undefined,
  options: { enabled: boolean; userId: string | null },
) {
  const status = useLocationTrackerStatus();
  const [settings, setSettings] = useState<LocationTrackingSettings | null>(null);
  const attendanceId = attendance?.attendanceId ?? null;
  const open = isAttendanceSessionOpen(attendance);
  const mode = attendance?.attendanceMode ?? null;

  useEffect(() => {
    if (!options.enabled || !attendanceId || !mode) return;
    let cancelled = false;
    loadTrackingSettings().then(next => {
      if (!cancelled) setSettings(next);
    });
    return () => {
      cancelled = true;
    };
  }, [options.enabled, attendanceId, mode]);

  const tracked = isTrackedAttendanceSource(mode, settings);

  useEffect(() => {
    if (!options.enabled || !attendanceId || !tracked) return;
    const tracker = getLocationTracker();
    if (open) {
      void tracker.start(attendanceId, { userId: options.userId });
    } else if (tracker.getStatus().attendanceId === attendanceId) {
      void tracker.stop();
    }
  }, [options.enabled, options.userId, attendanceId, open, tracked]);

  return { status, tracked, settings };
}
